import { useEffect, useRef, useState } from 'react';
import {
  AnimatePresence,
  motion,
  useMotionValue,
  useReducedMotion,
  useScroll,
  useSpring,
  useTransform,
} from 'framer-motion';
import { useExperience, useFinePointer, useLive } from '@/lib/experience';
import { LOCATION, HERO_SLIDES } from '@/data/site';
import { RevealWords } from './fx/RevealWords';
import { Magnetic } from './fx/Magnetic';
import { Bubbles } from './fx/Bubbles';
import { Photo } from './fx/Photo';
import { burst } from './fx/Burst';
import { Paw, Bow, BowTie, Bone, Note, Cloud, Sparkle, Heart, Star, DoodleArrow } from './brand/Shapes';

/** Enfeite com parallax do mouse (desktop) e flutuação suave (sempre). */
const Floaty = ({ mx, my, depth = 1, className = '', rotate = 0, delay = 0, float = 4, children, play, live = true }) => {
  const x = useTransform(mx, [-1, 1], [-22 * depth, 22 * depth]);
  const y = useTransform(my, [-1, 1], [-16 * depth, 16 * depth]);
  return (
    <motion.div className={`absolute pointer-events-none ${className}`} style={{ x, y }}>
      <motion.div
        initial={{ scale: 0, rotate: rotate - 40 }}
        animate={play ? { scale: 1, rotate } : {}}
        transition={{ type: 'spring', stiffness: 300, damping: 11, delay }}
      >
        <motion.div animate={live ? { y: [0, -10, 0], rotate: [0, 6, -6, 0] } : { y: 0, rotate: 0 }} transition={{ duration: float, repeat: Infinity, ease: 'easeInOut' }}>
          {children}
        </motion.div>
      </motion.div>
    </motion.div>
  );
};

/**
 * Fotos passando dentro do círculo: cada nova entra abrindo como uma bolha,
 * com um leve zoom lento no rosto do pet (a anterior só some depois de coberta).
 */
const HeroSlides = ({ index, reduce }) =>
  HERO_SLIDES.map((slide, i) => {
    const on = i === index;
    return (
      <motion.div
        key={slide.photo.name}
        className="absolute inset-0"
        style={{ zIndex: on ? 2 : 1 }}
        initial={false}
        animate={{ clipPath: on ? 'circle(75% at 50% 50%)' : 'circle(0% at 50% 50%)' }}
        transition={on ? { duration: reduce ? 0 : 1.1, ease: [0.34, 1.2, 0.64, 1] } : { delay: reduce ? 0 : 1.1, duration: 0 }}
      >
        <motion.div
          className="w-full h-full"
          style={{ transformOrigin: slide.pos }}
          animate={{ scale: on && !reduce ? [slide.zoom, slide.zoom * 1.07] : slide.zoom }}
          transition={{ duration: 4.6, ease: 'linear' }}
        >
          <Photo
            photo={slide.photo}
            priority={i === 0}
            eager
            alt={on ? slide.photo.alt : ''}
            sizes="(min-width: 1024px) 460px, 80vw"
            className="w-full h-full object-cover"
            style={{ objectPosition: slide.pos }}
          />
        </motion.div>
      </motion.div>
    );
  });

/** Faixa com pontas dobradas — a mesma forma do banner do logo. */
const Ribbon = ({ children, play }) => (
  <motion.div
    className="absolute left-[11%] bottom-[1%] w-[78%] z-20"
    initial={{ scaleX: 0, rotate: -6 }}
    animate={play ? { scaleX: 1, rotate: -3 } : {}}
    transition={{ type: 'spring', stiffness: 220, damping: 12, delay: 0.9 }}
  >
    <svg viewBox="0 0 400 96" className="w-full drop-shadow-[0_10px_16px_rgba(47,116,224,0.35)]" aria-hidden="true">
      <polygon points="0,30 72,30 72,88 0,88 22,59" fill="#2F74E0" />
      <polygon points="400,30 328,30 328,88 400,88 378,59" fill="#2F74E0" />
      <polygon points="52,74 72,88 72,74" fill="#1E56B0" />
      <polygon points="348,74 328,88 328,74" fill="#1E56B0" />
      <path d="M52 12 Q200 -2 348 12 L348 74 Q200 60 52 74 Z" fill="#4D96FF" />
      <path d="M62 18 Q200 5 338 18" stroke="rgba(255,255,255,.35)" strokeWidth="3" fill="none" strokeLinecap="round" />
    </svg>
    <span className="absolute inset-x-0 top-[12%] bottom-[30%] flex items-center justify-center font-display text-white text-[clamp(1rem,2.4vw,1.8rem)] leading-none">
      {children}
    </span>
  </motion.div>
);

export const Hero = () => {
  const { ready, scrollTo } = useExperience();
  const reduce = useReducedMotion();
  const fine = useFinePointer();
  const ref = useRef(null);
  const [pops, setPops] = useState(0);
  const [woof, setWoof] = useState(0);
  const [slide, setSlide] = useState(0);
  const live = useLive(ref);

  useEffect(() => {
    if (!ready || !live || reduce) return undefined;
    const id = setInterval(() => setSlide((s) => (s + 1) % HERO_SLIDES.length), 4200);
    return () => clearInterval(id);
  }, [ready, live, reduce, slide]);

  const { scrollYProgress } = useScroll({ target: ref, offset: ['start start', 'end start'] });
  const photoScale = useTransform(scrollYProgress, [0, 1], [1, 1.25]);
  const contentY = useTransform(scrollYProgress, [0, 1], ['0%', '-20%']);
  const badgeY = useTransform(scrollYProgress, [0, 1], ['0%', '12%']);
  const ringRotate = useTransform(scrollYProgress, [0, 1], [0, 140]);

  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const smx = useSpring(mx, { stiffness: 70, damping: 16 });
  const smy = useSpring(my, { stiffness: 70, damping: 16 });
  const tiltX = useTransform(smy, [-1, 1], [8, -8]);
  const tiltY = useTransform(smx, [-1, 1], [-10, 10]);

  const onPointerMove = (e) => {
    if (!fine || reduce) return;
    mx.set((e.clientX / window.innerWidth - 0.5) * 2);
    my.set((e.clientY / window.innerHeight - 0.5) * 2);
  };

  const play = ready;

  return (
    <section
      id="inicio"
      data-nav="inicio"
      ref={ref}
      onPointerMove={onPointerMove}
      data-paused={!live}
      className="relative min-h-[100svh] overflow-hidden bg-pet-cream flex flex-col"
    >
      {/* blobs pastéis que respiram */}
      <div className="absolute inset-0 pointer-events-none [mask-image:linear-gradient(to_bottom,#000_72%,transparent)]" aria-hidden="true">
        <div className="blob absolute -top-[12%] -right-[10%] w-[58vw] h-[58vw] max-w-[820px] max-h-[820px] bg-pet-sky" />
        <div className="blob absolute top-[45%] -left-[14%] w-[36vw] h-[36vw] max-w-[520px] max-h-[520px] bg-pet-pink/45 [animation-delay:-4s]" />
        <div className="blob absolute -bottom-[16%] left-[34%] w-[30vw] h-[30vw] max-w-[440px] max-h-[440px] bg-pet-mint/80 [animation-delay:-8s]" />
        <div className="blob absolute top-[10%] left-[38%] w-[12vw] h-[12vw] max-w-[180px] max-h-[180px] bg-pet-peach/80 [animation-delay:-2s]" />
      </div>

      {/* enfeites do site original: patinhas, corações, ossinhos e estrelinhas */}
      <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
        <Floaty live={live} mx={smx} my={smy} depth={1.2} play={play} delay={0.5} className="left-[4%] top-[18%] w-8 text-pet-blue/40"><Paw className="w-full" /></Floaty>
        <Floaty live={live} mx={smx} my={smy} depth={0.8} play={play} delay={0.6} float={5} className="left-[44%] top-[14%] w-6 text-pet-pink-deep/50"><Heart className="w-full" /></Floaty>
        <Floaty live={live} mx={smx} my={smy} depth={1.6} play={play} delay={0.7} float={6} className="left-[8%] bottom-[16%] w-12 text-pet-peach"><Bone className="w-full" /></Floaty>
        <Floaty live={live} mx={smx} my={smy} depth={1} play={play} delay={0.8} float={4.5} className="left-[52%] bottom-[12%] w-7 text-pet-yellow"><Star className="w-full" /></Floaty>
      </div>

      <Bubbles count={fine ? 12 : 7} onPop={() => setPops((p) => p + 1)} />

      <motion.div
        style={{ y: reduce ? 0 : contentY }}
        className="container-cp relative z-10 flex-1 grid lg:grid-cols-12 gap-10 lg:gap-6 items-center pt-28 md:pt-32 pb-8 pointer-events-none"
      >
        <div className="lg:col-span-7">
          <motion.p
            className="kicker kicker-ears bg-pet-mint text-pet-mint-ink mb-5 md:mb-7"
            initial={{ opacity: 0, scale: 0.5, rotate: -8 }}
            animate={play ? { opacity: 1, scale: 1, rotate: -2 } : {}}
            transition={{ type: 'spring', stiffness: 400, damping: 12, delay: 0.2 }}
          >
            <Sparkle className="w-4 h-4" />
            <span>
              Banho, tosa e passeios<span className="hidden sm:inline"> em {LOCATION.replace(' - ', '/')}</span>
            </span>
          </motion.p>

          <h1 className="font-display font-medium text-pet-ink text-[clamp(2.6rem,10.5vw,4.2rem)] lg:text-[clamp(3.6rem,4.9vw,5.6rem)] leading-[1.06]">
            <RevealWords
              as="span"
              play={play}
              delay={0.25}
              stagger={0.08}
              parts={[
                { text: 'Seu pet merece' },
                { text: 'todo carinho', className: 'sticker-halo text-transparent bg-clip-text bg-gradient-to-r from-pet-blue via-[#8E9BFF] to-pet-pink-deep pb-1' },
                { text: 'do mundo' },
              ]}
            />
            <motion.span
              className="inline-block align-middle ml-2 w-[0.8em] h-[0.8em] text-pet-pink-deep"
              initial={{ scale: 0 }}
              animate={play ? { scale: 1 } : {}}
              transition={{ type: 'spring', stiffness: 500, damping: 10, delay: 1 }}
            >
              <Heart className="w-full h-full heartbeat" />
            </motion.span>
          </h1>

          <motion.p
            className="mt-6 md:mt-8 max-w-[34rem] text-lg md:text-xl leading-relaxed text-pet-ink/75 font-semibold"
            initial={{ opacity: 0, y: 16 }}
            animate={play ? { opacity: 1, y: 0 } : {}}
            transition={{ type: 'spring', stiffness: 200, damping: 20, delay: 0.8 }}
          >
            Oferecemos banho e tosa com muito amor e profissionalismo. Aqui seu pet é tratado como{' '}
            <span className="highlight text-pet-ink font-extrabold whitespace-nowrap">parte da família!</span>
          </motion.p>

          <motion.div
            className="mt-8 flex flex-wrap items-center gap-2.5 sm:gap-4 pointer-events-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={play ? { opacity: 1, y: 0 } : {}}
            transition={{ type: 'spring', stiffness: 220, damping: 16, delay: 0.95 }}
          >
            <Magnetic>
              <button
                type="button"
                onClick={(e) => {
                  burst(e, 'paw');
                  scrollTo('banho');
                }}
                className="btn-blue !px-4 sm:!px-7 !text-[15px] sm:!text-base"
              >
                <Paw className="w-5 h-5" />
                Ver pacotes
              </button>
            </Magnetic>
            <Magnetic>
              <button
                type="button"
                onClick={(e) => {
                  burst(e, 'heart');
                  scrollTo('contato');
                }}
                className="btn-pink !px-4 sm:!px-7 !text-[15px] sm:!text-base"
              >
                <Heart className="w-4 h-4" />
                Fale conosco
              </button>
            </Magnetic>
          </motion.div>
        </div>

        {/* Selo: foto real dentro do "céu" do logo */}
        <motion.div
          className="lg:col-span-5 relative w-full max-w-[330px] sm:max-w-[420px] lg:max-w-[540px] mx-auto pointer-events-auto"
          style={{ y: reduce ? 0 : badgeY, perspective: 1000 }}
        >
          <motion.div
            className="relative aspect-square"
            style={{ rotateX: tiltX, rotateY: tiltY, transformStyle: 'preserve-3d' }}
            initial={{ scale: 0.3, opacity: 0, rotate: -20 }}
            animate={play ? { scale: 1, opacity: 1, rotate: 0 } : {}}
            transition={{ type: 'spring', stiffness: 160, damping: 13, delay: 0.3 }}
          >
            <motion.div className="absolute inset-0" style={{ rotate: reduce ? 0 : ringRotate }}>
              <svg viewBox="0 0 500 500" className="w-full h-full animate-[spin_40s_linear_infinite]" aria-hidden="true">
                <defs>
                  <path id="hero-ring" d="M250,250 m-232,0 a232,232 0 1,1 464,0 a232,232 0 1,1 -464,0" />
                </defs>
                <text style={{ fontSize: 21, letterSpacing: 6, fontFamily: 'DynaPuff, sans-serif', fill: '#4D96FF' }}>
                  <textPath href="#hero-ring">banho ♥ tosa ♥ passeio ♥ carinho ♥ qualidade ♥ confiança ♥ </textPath>
                </text>
              </svg>
            </motion.div>

            {/* céu do logo */}
            <div className="absolute inset-[8%] rounded-full overflow-hidden bg-[radial-gradient(circle_at_50%_30%,#E8F4FD_0%,#9FD0FF_45%,#4D96FF_100%)] shadow-[0_30px_60px_-24px_rgba(77,150,255,0.8)]">
              <Cloud className="absolute w-[34%] left-[-6%] top-[18%] text-white/90" />
              <Cloud className="absolute w-[26%] right-[-4%] top-[8%] text-white/80" />
              <Note className="absolute w-[6%] left-[18%] top-[8%] text-white/70" />
              <Note className="absolute w-[5%] right-[16%] top-[30%] text-white/60" />
              <Paw className="absolute w-[7%] left-[8%] bottom-[36%] text-white/50 -rotate-12" />
            </div>

            {/* foto */}
            <motion.div
              className="absolute inset-[13%] rounded-full overflow-hidden ring-[7px] ring-white shadow-2xl cursor-pointer"
              data-cursor="Au-au!"
              whileTap={{ scale: 0.95 }}
              onClick={(e) => {
                burst(e, 'heart', 12);
                setWoof((w) => w + 1);
                setSlide((s) => (s + 1) % HERO_SLIDES.length);
              }}
            >
              <motion.div className="relative w-full h-full" style={{ scale: reduce ? 1 : photoScale }}>
                <HeroSlides index={slide} reduce={reduce} />
              </motion.div>
            </motion.div>

            {/* balão de fala */}
            <AnimatePresence mode="wait">
              {play && (
                <motion.div
                  key={woof}
                  className="absolute right-[2%] sm:right-[-6%] top-[-4%] z-30 rounded-[20px] rounded-bl-md bg-white px-4 py-2 shadow-xl font-display text-pet-blue text-lg md:text-xl ring-2 ring-pet-sky"
                  initial={{ scale: 0, rotate: -20, originX: 0, originY: 1 }}
                  animate={{ scale: 1, rotate: 6 }}
                  exit={{ scale: 0 }}
                  transition={{ type: 'spring', stiffness: 500, damping: 12, delay: woof ? 0 : 1.6 }}
                >
                  {woof % 2 ? 'Oba! 💙' : 'Au-au! 🐾'}
                </motion.div>
              )}
            </AnimatePresence>

            <Ribbon play={play}>Cuidado com amor</Ribbon>

            {/* card do hero original */}
            <motion.div
              className="absolute left-[-5%] top-[56%] z-20 hidden sm:flex items-center gap-3 rounded-[20px] bg-white/95 pl-2 pr-4 py-2 shadow-xl -rotate-3"
              initial={{ opacity: 0, x: -30, scale: 0.6 }}
              animate={play ? { opacity: 1, x: 0, scale: 1 } : {}}
              transition={{ type: 'spring', stiffness: 300, damping: 14, delay: 1.3 }}
              style={{ translateZ: 40 }}
            >
              <span className="w-11 h-11 rounded-2xl bg-gradient-to-br from-pet-blue to-pet-pink text-white flex items-center justify-center">
                <Sparkle className="w-5 h-5" />
              </span>
              <span className="leading-tight">
                <span className="block font-display text-[17px] text-pet-ink">Banho Completo</span>
                <span className="block text-xs font-bold text-pet-muted">Hidratação + Perfume</span>
              </span>
            </motion.div>
          </motion.div>

          {/* adesivos: laços e gravatinhas das fotos pós-banho */}
          <Floaty live={live} mx={smx} my={smy} depth={1.6} play={play} delay={1.1} float={4.5} rotate={-18} className="left-[-3%] top-[3%] w-[22%]">
            <Bow className="w-full text-pet-pink-deep drop-shadow-[0_8px_10px_rgba(255,138,155,0.5)]" />
          </Floaty>
          <Floaty live={live} mx={smx} my={smy} depth={2.2} play={play} delay={1.25} float={5.5} rotate={14} className="right-[-4%] bottom-[22%] w-[20%]">
            <BowTie dots className="w-full text-pet-blue drop-shadow-[0_8px_10px_rgba(77,150,255,0.45)]" />
          </Floaty>
          <Floaty live={live} mx={smx} my={smy} depth={1} play={play} delay={1.4} float={3.8} rotate={-12} className="left-[2%] bottom-[8%] w-[13%]">
            <Bone className="w-full text-pet-peach drop-shadow-[0_6px_8px_rgba(0,0,0,0.12)]" />
          </Floaty>
        </motion.div>
      </motion.div>

      {/* rodapé do hero */}
      <motion.div
        className="container-cp relative z-10 pb-6 md:pb-8 flex items-center justify-center md:justify-between gap-4"
        initial={{ opacity: 0 }}
        animate={play ? { opacity: 1 } : {}}
        transition={{ delay: 1.5, duration: 0.8 }}
      >
        <span className="hidden md:inline-flex kicker bg-white text-pet-ink/80 shadow-sm">📍 {LOCATION}</span>
        <span className="flex items-center gap-2 min-h-[28px]" aria-live="polite">
          <DoodleArrow className="w-10 h-7 text-pet-pink-deep -scale-x-100 rotate-[-20deg]" />
          {pops === 0 ? (
            <span className="font-hand text-xl md:text-2xl text-pet-blue">psiu… estoure as bolhas de sabão!</span>
          ) : (
            <span className="font-display text-pet-pink-deep text-lg inline-flex items-center gap-1.5">
              <motion.span
                key={pops}
                className="inline-block tabular-nums"
                initial={{ scale: 1.8, rotate: -15 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ type: 'spring', stiffness: 600, damping: 12 }}
              >
                {pops}
              </motion.span>
              {pops === 1 ? 'bolha estourada' : 'bolhas estouradas'}
              {pops >= 10 ? ' — que banho! 🛁' : ' 🫧'}
            </span>
          )}
        </span>
        <span className="hidden md:inline-flex kicker bg-white text-pet-ink/80 shadow-sm">
          <Paw className="w-3.5 h-3.5 text-pet-pink-deep" /> role pra ver mais
        </span>
      </motion.div>
    </section>
  );
};

export default Hero;
